import { useState } from 'react'
import { Download, ExternalLink, FileText } from 'lucide-react'
import { Button, Modal, Select, Toggle } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption } from '@/components/ui'
import { WorkspaceToolbarSearch } from '@/components/common/WorkspaceToolbar'
import { EmptyState } from '@/components/common/EmptyState'
import { toast } from 'sonner'
import {
  downloadDocument,
  openDocumentInNewTab,
  type DocumentCategory,
  type StaffDocument,
} from '@/api/services/documents.service'
import {
  formatDocumentCategory,
  formatFileSize,
  getDocumentCategoryOptions,
} from '@/features/documents/utils/documentUtils'
import { useAllStaffDocuments } from '../hooks/useUserDocuments'

const CATEGORY_OPTIONS = getDocumentCategoryOptions(true, 'staff')

function ownerLabel(document: StaffDocument): string {
  const owner = document.owner
  if (!owner) return document.userId ?? 'Unknown employee'
  return `${owner.firstName} ${owner.lastName}`.trim() || owner.email
}

function ownerSubline(document: StaffDocument): string {
  const owner = document.owner
  if (!owner) return 'User record unavailable'
  return [owner.employeeId, owner.email].filter(Boolean).join(' · ')
}

export interface AllStaffDocumentsModalProps {
  open: boolean
  onClose: () => void
  companyOptions?: SelectOption[]
}

export function AllStaffDocumentsModal({
  open,
  onClose,
  companyOptions = [],
}: AllStaffDocumentsModalProps): React.ReactElement {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [includeArchived, setIncludeArchived] = useState(false)
  const documentsQuery = useAllStaffDocuments(
    {
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(category ? { category: category as DocumentCategory } : {}),
      ...(companyId ? { companyId } : {}),
      includeArchived,
    },
    { enabled: open },
  )
  const documents = documentsQuery.data ?? []

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="All staff documents"
      className="max-w-5xl"
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem_14rem_auto] md:items-center">
          <WorkspaceToolbarSearch
            value={search}
            onChange={setSearch}
            placeholder="Search by employee name, email, or ID..."
            ariaLabel="Search staff documents"
            active={Boolean(search.trim())}
          />
          <Select
            options={CATEGORY_OPTIONS}
            value={category}
            onChange={setCategory}
            placeholder="All categories"
          />
          <Select
            options={[{ value: '', label: 'All companies' }, ...companyOptions]}
            value={companyId}
            onChange={setCompanyId}
            placeholder="All companies"
            searchable
          />
          <Toggle
            checked={includeArchived}
            onChange={setIncludeArchived}
            label="Archived"
          />
        </div>

        {documentsQuery.isLoading ? (
          <SectionLoader variant="list" label="Loading staff documents" />
        ) : documents.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No documents found"
            description="Try a different employee, category, or company filter."
          />
        ) : (
          <div className="max-h-[65vh] overflow-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="sticky top-0 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-3">Document</th>
                  <th className="px-3 py-3">Employee</th>
                  <th className="px-3 py-3">Category</th>
                  <th className="px-3 py-3">Size</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {documents.map((document) => (
                  <tr key={document.id}>
                    <td className="min-w-0 px-3 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 shrink-0 text-gray-500" />
                        <span className="min-w-0 truncate font-medium text-gray-900 dark:text-gray-100">
                          {document.originalName}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {ownerLabel(document)}
                      </p>
                      <p className="text-xs text-gray-500">{ownerSubline(document)}</p>
                    </td>
                    <td className="px-3 py-3 text-gray-600 dark:text-gray-300">
                      {document.documentType || formatDocumentCategory(document.category)}
                    </td>
                    <td className="px-3 py-3 text-gray-600 dark:text-gray-300">
                      {formatFileSize(document.size)}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-8 min-h-8 px-2"
                          onClick={() => {
                            void openDocumentInNewTab(document).catch(() => {
                              toast.error(`Could not open ${document.originalName}`)
                            })
                          }}
                          aria-label={`Open ${document.originalName}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-8 min-h-8 px-2"
                          onClick={() => void downloadDocument(document)}
                          aria-label={`Download ${document.originalName}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  )
}

AllStaffDocumentsModal.displayName = 'AllStaffDocumentsModal'
